const VALID_APP_ENVS = Object.freeze(['local', 'dev', 'staging', 'production']);
const LEGACY_TRANSITION_FIREBASE_PROJECT_ID = 'juniperai';
const LEGACY_TRANSITION_PROJECT_NUMBER = '924303952632';
const LEGACY_TRANSITION_STORAGE_BUCKETS = Object.freeze([
  'juniperai.appspot.com',
  'juniperai.firebasestorage.app',
]);
const LEGACY_TRANSITION_ORIGIN = 'https://juniperai.uc.r.appspot.com';
const FORBIDDEN_PROJECT_IDS = Object.freeze([
  'featherglass-dev',
  '3995060887',
  'featherglass-app',
]);
const FORBIDDEN_PROJECT_NUMBERS = Object.freeze(['3995060887', '754751941055']);
const FORBIDDEN_FIREBASE_API_KEYS = Object.freeze([
  'AIzaSyAHOxGCKUYfc_3hDG7zqK62SR0a3FT7HC0',
]);

const ALLOWED_PAYLOAD_KEYS = Object.freeze([
  'appEnv',
  'apiBaseUrl',
  'frontendUrl',
  'appUrl',
  'firebase',
  'stripePublishableKey',
]);
const ALLOWED_FIREBASE_KEYS = Object.freeze([
  'apiKey',
  'authDomain',
  'projectId',
  'storageBucket',
  'messagingSenderId',
  'appId',
  'measurementId',
]);

const PROJECT_ID_RE = /^[a-z][a-z0-9-]{4,29}$/;
const NUMERIC_PROJECT_RE = /^\d{6,25}$/;
const BUCKET_NAME_RE = /^[a-z0-9][a-z0-9._-]{1,220}[a-z0-9]$/;
const FIREBASE_WEB_API_KEY_RE = /^AIza[0-9A-Za-z_-]{20,60}$/;
const FIREBASE_AUTH_DOMAIN_RE = /^[a-z0-9][a-z0-9.-]{1,251}[a-z0-9]$/i;
const FIREBASE_WEB_APP_ID_RE = /^1:(\d{6,25}):web:([a-f0-9]{8,64})$/i;
const FIREBASE_MEASUREMENT_ID_RE = /^G-[A-Z0-9]{4,20}$/i;
const STRIPE_PUBLISHABLE_KEY_RE = /^pk_(test|live)_[0-9A-Za-z_-]+$/;

const hasValue = (value) => typeof value === 'string' && value.trim().length > 0;

export function frontendConfigError(message) {
  const error = new Error(message);
  error.code = 'FRONTEND_RUNTIME_CONFIGURATION';
  return error;
}

function isLocalHostname(hostname) {
  return hostname === 'localhost' || hostname === '127.0.0.1' || hostname === '[::1]';
}

function looksLikeSecretValue(value) {
  const str = String(value || '').trim();
  if (!str) return false;
  return (
    str.startsWith('sk_live_') ||
    str.startsWith('sk_test_') ||
    str.startsWith('rk_live_') ||
    str.startsWith('rk_test_') ||
    str.startsWith('whsec_') ||
    str.startsWith('SG.') ||
    str.includes('-----BEGIN') ||
    str.includes('PRIVATE KEY')
  );
}

function assertNotForbiddenProject(value, fieldName) {
  const normalized = String(value || '').trim().toLowerCase();
  if (
    FORBIDDEN_PROJECT_IDS.includes(normalized) ||
    FORBIDDEN_PROJECT_IDS.some((id) => normalized.startsWith(`${id}.`))
  ) {
    throw frontendConfigError(
      `Invalid ${fieldName}: populated legacy retirement project (${FORBIDDEN_PROJECT_IDS.join(', ')}) cannot be selected as an active target.`
    );
  }
}

function normalizeOriginUrl(rawValue, fieldName) {
  const trimmed = String(rawValue || '').trim();
  if (!trimmed || trimmed === '*') {
    throw frontendConfigError(`Invalid ${fieldName}: wildcard or empty origin is not permitted.`);
  }
  let parsed;
  try {
    parsed = new URL(trimmed);
  } catch {
    throw frontendConfigError(`Invalid ${fieldName}: must be a valid http(s) origin URL.`);
  }
  if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') {
    throw frontendConfigError(`Invalid ${fieldName}: protocol must be http: or https:.`);
  }
  if (parsed.username || parsed.password || parsed.search || parsed.hash) {
    throw frontendConfigError(
      `Invalid ${fieldName}: origin URL must not include credentials, query parameters, or fragments.`
    );
  }
  if (parsed.pathname && parsed.pathname !== '/') {
    throw frontendConfigError(`Invalid ${fieldName}: origin URL must not include a path segment.`);
  }
  return parsed;
}

function assertOriginMatchesEnvironment(parsedUrl, fieldName, { appEnv, isLegacyTransitionPlane, role }) {
  const { origin, protocol, hostname } = parsedUrl;

  if (appEnv === 'local') {
    if (!isLocalHostname(hostname)) {
      throw frontendConfigError(`Origin mismatch in ${fieldName}: APP_ENV=local only permits localhost origins.`);
    }
    return origin;
  }

  if (isLocalHostname(hostname) || protocol !== 'https:') {
    throw frontendConfigError(
      `Origin mismatch in ${fieldName}: APP_ENV=${appEnv} requires an https non-localhost origin.`
    );
  }

  if (origin === LEGACY_TRANSITION_ORIGIN) {
    if (!isLegacyTransitionPlane) {
      throw frontendConfigError(
        `Origin mismatch in ${fieldName}: isolated environment cannot allow legacy ${LEGACY_TRANSITION_ORIGIN}.`
      );
    }
    if (role !== 'backend') {
      throw frontendConfigError(
        `Origin mismatch in ${fieldName}: ${LEGACY_TRANSITION_ORIGIN} is not a valid primary frontend/app URL.`
      );
    }
    return origin;
  }

  if (appEnv === 'dev') {
    if (origin === 'https://dev.featherglass.com') return origin;
    if (!isLegacyTransitionPlane && /^https:\/\/[a-z0-9-]+(?:\.[a-z0-9-]+)*\.run\.app$/.test(origin)) {
      return origin;
    }
    throw frontendConfigError(`Origin mismatch in ${fieldName}: origin is not permitted for APP_ENV=dev.`);
  }

  if (appEnv === 'staging') {
    if (origin === 'https://staging.featherglass.com') return origin;
    throw frontendConfigError(`Origin mismatch in ${fieldName}: origin is not permitted for APP_ENV=staging.`);
  }

  if (appEnv === 'production') {
    if (origin === 'https://secure.featherglass.com') return origin;
    throw frontendConfigError(`Origin mismatch in ${fieldName}: origin is not permitted for APP_ENV=production.`);
  }

  throw frontendConfigError(`Origin mismatch in ${fieldName}.`);
}

function validateAuthDomain(authDomain, { appEnv, projectId, isLegacyTransitionPlane }) {
  if (!hasValue(authDomain)) {
    throw frontendConfigError('Missing required frontend Firebase configuration: authDomain.');
  }
  const trimmed = authDomain.trim();
  if (!FIREBASE_AUTH_DOMAIN_RE.test(trimmed) || trimmed.includes('..')) {
    throw frontendConfigError('Invalid firebase.authDomain: must be a valid hostname without protocol, port, or path.');
  }
  assertNotForbiddenProject(trimmed, 'firebase.authDomain');
  const lower = trimmed.toLowerCase();
  if (lower.includes('juniperai0808')) {
    throw frontendConfigError('Cross-project mismatch in firebase.authDomain: cannot reference juniperai0808.');
  }

  const expectedFirebaseAppDomain = `${projectId}.firebaseapp.com`;
  const expectedWebAppDomain = `${projectId}.web.app`;
  const isCustomFeatherglassDomain = lower === 'featherglass.com' || lower.endsWith('.featherglass.com');

  if (isLegacyTransitionPlane) {
    if (lower !== expectedFirebaseAppDomain && lower !== expectedWebAppDomain && !isCustomFeatherglassDomain) {
      throw frontendConfigError(
        `Cross-project mismatch in firebase.authDomain: must match ${expectedFirebaseAppDomain}, ${expectedWebAppDomain}, or an approved featherglass.com domain.`
      );
    }
  } else {
    if (lower.includes('juniperai')) {
      throw frontendConfigError(
        'Cross-project mismatch in firebase.authDomain: isolated environment cannot use legacy juniperai authDomain.'
      );
    }
    if (lower !== expectedFirebaseAppDomain && lower !== expectedWebAppDomain && !isCustomFeatherglassDomain) {
      throw frontendConfigError(
        `Cross-project mismatch in firebase.authDomain: must match ${expectedFirebaseAppDomain}, ${expectedWebAppDomain}, or an approved featherglass.com domain.`
      );
    }
    if (isCustomFeatherglassDomain) {
      if ((appEnv === 'staging' || appEnv === 'production') && /(^|\.)dev\./i.test(lower)) {
        throw frontendConfigError(`Cross-environment mismatch in firebase.authDomain: APP_ENV=${appEnv} cannot use a dev authDomain.`);
      }
      if ((appEnv === 'local' || appEnv === 'dev') && /(^|\.)(staging|secure|prod|production)\./i.test(lower)) {
        throw frontendConfigError(`Cross-environment mismatch in firebase.authDomain: APP_ENV=${appEnv} cannot use a production/staging authDomain.`);
      }
    }
  }

  return trimmed;
}

function validateStorageBucket(bucketName, { projectId, isLegacyTransitionPlane }) {
  if (!hasValue(bucketName)) {
    throw frontendConfigError('Missing required frontend Firebase configuration: storageBucket.');
  }
  const trimmed = bucketName.trim();
  assertNotForbiddenProject(trimmed, 'firebase.storageBucket');
  if (!BUCKET_NAME_RE.test(trimmed)) {
    throw frontendConfigError('Invalid firebase.storageBucket: must be a valid Cloud Storage bucket name.');
  }
  if (trimmed.toLowerCase().includes('juniperai0808')) {
    throw frontendConfigError('Cross-project mismatch in firebase.storageBucket: cannot reference juniperai0808.');
  }

  if (isLegacyTransitionPlane) {
    if (!LEGACY_TRANSITION_STORAGE_BUCKETS.includes(trimmed)) {
      throw frontendConfigError(
        `Cross-project mismatch in firebase.storageBucket: legacy transition bucket must be one of ${LEGACY_TRANSITION_STORAGE_BUCKETS.join(', ')}.`
      );
    }
  } else {
    if (LEGACY_TRANSITION_STORAGE_BUCKETS.includes(trimmed) || trimmed.toLowerCase().includes('juniperai')) {
      throw frontendConfigError(
        'Cross-project mismatch in firebase.storageBucket: isolated environment cannot use a legacy juniperai bucket.'
      );
    }
    if (
      !trimmed.startsWith(`${projectId}.`) &&
      !trimmed.startsWith(`${projectId}-`) &&
      trimmed !== projectId
    ) {
      throw frontendConfigError(
        `Cross-project mismatch in firebase.storageBucket: bucket must belong to project ${projectId}.`
      );
    }
  }

  return trimmed;
}

function validateLocationAlignment(appEnv, currentLocation) {
  if (!currentLocation || !hasValue(currentLocation.hostname)) return;
  const hostname = currentLocation.hostname.trim().toLowerCase();

  if (hostname === 'secure.featherglass.com' && appEnv !== 'production') {
    throw frontendConfigError(
      `Browser hostname mismatch: secure.featherglass.com requires appEnv="production", received "${appEnv}".`
    );
  }
  if (hostname === 'staging.featherglass.com' && appEnv !== 'staging') {
    throw frontendConfigError(
      `Browser hostname mismatch: staging.featherglass.com requires appEnv="staging", received "${appEnv}".`
    );
  }
  if (hostname === 'dev.featherglass.com' && appEnv !== 'dev') {
    throw frontendConfigError(
      `Browser hostname mismatch: dev.featherglass.com requires appEnv="dev", received "${appEnv}".`
    );
  }
  if (appEnv === 'local' && !isLocalHostname(hostname)) {
    throw frontendConfigError(
      `Browser hostname mismatch: appEnv="local" is only permitted on localhost, not "${hostname}".`
    );
  }
}

export function validateFrontendRuntimeConfig(payload, { currentLocation = null } = {}) {
  if (!payload || typeof payload !== 'object' || Array.isArray(payload)) {
    throw frontendConfigError('Invalid frontend runtime configuration payload: expected an object.');
  }

  for (const key of Object.keys(payload)) {
    if (!ALLOWED_PAYLOAD_KEYS.includes(key)) {
      throw frontendConfigError(`Invalid frontend runtime configuration payload: unexpected property "${key}".`);
    }
  }

  if (!hasValue(payload.appEnv) || !VALID_APP_ENVS.includes(payload.appEnv.trim())) {
    throw frontendConfigError(
      `Invalid frontend runtime configuration payload: appEnv must be one of ${VALID_APP_ENVS.join(', ')}.`
    );
  }
  const appEnv = payload.appEnv.trim();

  validateLocationAlignment(appEnv, currentLocation);

  if (!payload.firebase || typeof payload.firebase !== 'object' || Array.isArray(payload.firebase)) {
    throw frontendConfigError('Invalid frontend runtime configuration payload: missing firebase configuration object.');
  }

  for (const key of Object.keys(payload.firebase)) {
    if (!ALLOWED_FIREBASE_KEYS.includes(key)) {
      throw frontendConfigError(`Invalid frontend Firebase configuration: unexpected property "${key}".`);
    }
  }

  const requiredFirebaseFields = [
    'apiKey',
    'authDomain',
    'projectId',
    'storageBucket',
    'messagingSenderId',
    'appId',
  ];
  const missingFirebaseFields = requiredFirebaseFields.filter((field) => !hasValue(payload.firebase[field]));
  if (missingFirebaseFields.length > 0) {
    throw frontendConfigError(
      `Missing required frontend Firebase configuration: ${missingFirebaseFields.join(', ')}.`
    );
  }

  const projectId = payload.firebase.projectId.trim();
  assertNotForbiddenProject(projectId, 'firebase.projectId');
  if (!PROJECT_ID_RE.test(projectId) || projectId === 'juniperai0808') {
    throw frontendConfigError('Invalid firebase.projectId: must be a valid Firebase project ID.');
  }

  const isLegacyTransitionPlane = projectId === LEGACY_TRANSITION_FIREBASE_PROJECT_ID;
  if (!isLegacyTransitionPlane) {
    if ((appEnv === 'staging' || appEnv === 'production') && /(^|-)dev(-|$)/i.test(projectId)) {
      throw frontendConfigError(
        `Inconsistent appEnv and firebase.projectId: APP_ENV=${appEnv} cannot target a development project.`
      );
    }
    if ((appEnv === 'local' || appEnv === 'dev') && /(^|-)(prod|production|staging|secure)(-|$)/i.test(projectId)) {
      throw frontendConfigError(
        `Inconsistent appEnv and firebase.projectId: APP_ENV=${appEnv} cannot target a production project.`
      );
    }
  }

  const apiKey = payload.firebase.apiKey.trim();
  if (looksLikeSecretValue(apiKey) || !FIREBASE_WEB_API_KEY_RE.test(apiKey)) {
    throw frontendConfigError('Invalid firebase.apiKey: must be a valid public Firebase web API key.');
  }
  if (FORBIDDEN_FIREBASE_API_KEYS.includes(apiKey)) {
    throw frontendConfigError('Invalid firebase.apiKey: legacy retirement-project browser key cannot be used.');
  }

  const authDomain = validateAuthDomain(payload.firebase.authDomain, {
    appEnv,
    projectId,
    isLegacyTransitionPlane,
  });

  const storageBucket = validateStorageBucket(payload.firebase.storageBucket, {
    projectId,
    isLegacyTransitionPlane,
  });

  const messagingSenderId = payload.firebase.messagingSenderId.trim();
  if (!NUMERIC_PROJECT_RE.test(messagingSenderId)) {
    throw frontendConfigError('Invalid firebase.messagingSenderId: must be a numeric Google Cloud project number.');
  }
  if (FORBIDDEN_PROJECT_NUMBERS.includes(messagingSenderId)) {
    throw frontendConfigError('Invalid firebase.messagingSenderId: populated legacy retirement project cannot be targeted.');
  }
  if (isLegacyTransitionPlane && messagingSenderId !== LEGACY_TRANSITION_PROJECT_NUMBER) {
    throw frontendConfigError('Cross-project mismatch: firebase.messagingSenderId does not match juniperai.');
  }
  if (!isLegacyTransitionPlane && messagingSenderId === LEGACY_TRANSITION_PROJECT_NUMBER) {
    throw frontendConfigError(
      'Cross-project mismatch: isolated environment cannot use legacy juniperai firebase.messagingSenderId.'
    );
  }

  const appId = payload.firebase.appId.trim();
  const appIdMatch = FIREBASE_WEB_APP_ID_RE.exec(appId);
  if (!appIdMatch) {
    throw frontendConfigError('Invalid firebase.appId: must match 1:<messagingSenderId>:web:<appId>.');
  }
  if (appIdMatch[1] !== messagingSenderId) {
    throw frontendConfigError('Cross-project mismatch: firebase.appId project number does not match firebase.messagingSenderId.');
  }

  let measurementId;
  if (payload.firebase.measurementId !== undefined && payload.firebase.measurementId !== null) {
    if (!hasValue(payload.firebase.measurementId) || !FIREBASE_MEASUREMENT_ID_RE.test(payload.firebase.measurementId.trim())) {
      throw frontendConfigError('Invalid firebase.measurementId: must match G-<id> when provided.');
    }
    measurementId = payload.firebase.measurementId.trim();
  }

  if (!hasValue(payload.apiBaseUrl)) {
    throw frontendConfigError('Missing required frontend runtime configuration: apiBaseUrl.');
  }
  const apiBaseUrl = assertOriginMatchesEnvironment(
    normalizeOriginUrl(payload.apiBaseUrl, 'apiBaseUrl'),
    'apiBaseUrl',
    { appEnv, isLegacyTransitionPlane, role: 'backend' }
  );

  const frontendUrl = payload.frontendUrl !== undefined
    ? assertOriginMatchesEnvironment(
        normalizeOriginUrl(payload.frontendUrl, 'frontendUrl'),
        'frontendUrl',
        { appEnv, isLegacyTransitionPlane, role: 'frontend' }
      )
    : (appEnv === 'local' ? 'http://localhost:3000' : apiBaseUrl);

  const appUrl = payload.appUrl !== undefined
    ? assertOriginMatchesEnvironment(
        normalizeOriginUrl(payload.appUrl, 'appUrl'),
        'appUrl',
        { appEnv, isLegacyTransitionPlane, role: 'app' }
      )
    : frontendUrl;

  let stripePublishableKey;
  if (payload.stripePublishableKey !== undefined && payload.stripePublishableKey !== null) {
    if (!hasValue(payload.stripePublishableKey)) {
      throw frontendConfigError('Invalid stripePublishableKey: value cannot be blank when provided.');
    }
    const trimmedStripeKey = payload.stripePublishableKey.trim();
    if (looksLikeSecretValue(trimmedStripeKey) || !STRIPE_PUBLISHABLE_KEY_RE.test(trimmedStripeKey)) {
      throw frontendConfigError(
        'Invalid stripePublishableKey: must be a public Stripe publishable key (pk_test_... or pk_live_...).'
      );
    }
    if (!isLegacyTransitionPlane && (appEnv === 'local' || appEnv === 'dev') && trimmedStripeKey.startsWith('pk_live_')) {
      throw frontendConfigError(
        `Invalid stripePublishableKey: live Stripe publishable keys are not permitted in isolated APP_ENV=${appEnv}.`
      );
    }
    stripePublishableKey = trimmedStripeKey;
  }

  return Object.freeze({
    appEnv,
    apiBaseUrl,
    frontendUrl,
    appUrl,
    firebase: Object.freeze({
      apiKey,
      authDomain,
      projectId,
      storageBucket,
      messagingSenderId,
      appId,
      ...(measurementId ? { measurementId } : {}),
    }),
    ...(stripePublishableKey ? { stripePublishableKey } : {}),
  });
}

export function buildRuntimeConfigFromEnv(
  env = typeof process !== 'undefined' ? process.env : {},
  currentLocation = typeof window !== 'undefined' ? window.location : null
) {
  if (!env || typeof env !== 'object') return null;

  const reactAppFields = [
    'REACT_APP_APP_ENV',
    'REACT_APP_API_URL',
    'REACT_APP_FIREBASE_API_KEY',
    'REACT_APP_FIREBASE_AUTH_DOMAIN',
    'REACT_APP_FIREBASE_PROJECT_ID',
    'REACT_APP_FIREBASE_STORAGE_BUCKET',
    'REACT_APP_FIREBASE_MESSAGING_SENDER_ID',
    'REACT_APP_FIREBASE_APP_ID',
    'REACT_APP_FIREBASE_MEASUREMENT_ID',
    'REACT_APP_STRIPE_PUBLISHABLE_KEY',
  ];

  for (const name of reactAppFields) {
    if (env[name] !== undefined && !hasValue(env[name])) {
      throw frontendConfigError(`Invalid ${name}: value cannot be blank when set.`);
    }
  }

  const firebaseEnvFields = [
    'REACT_APP_FIREBASE_API_KEY',
    'REACT_APP_FIREBASE_AUTH_DOMAIN',
    'REACT_APP_FIREBASE_PROJECT_ID',
    'REACT_APP_FIREBASE_STORAGE_BUCKET',
    'REACT_APP_FIREBASE_MESSAGING_SENDER_ID',
    'REACT_APP_FIREBASE_APP_ID',
    'REACT_APP_FIREBASE_MEASUREMENT_ID',
  ];
  const hasAnyFirebaseEnvField = firebaseEnvFields.some((name) => hasValue(env[name]));
  if (!hasAnyFirebaseEnvField) {
    return null;
  }

  const appEnv = hasValue(env.REACT_APP_APP_ENV)
    ? env.REACT_APP_APP_ENV.trim()
    : (currentLocation && isLocalHostname(currentLocation.hostname) ? 'local' : '');

  const defaultLocalApiUrl = currentLocation?.protocol === 'https:'
    ? 'https://localhost:8080'
    : 'http://localhost:8080';
  const apiBaseUrl = hasValue(env.REACT_APP_API_URL)
    ? env.REACT_APP_API_URL.trim()
    : (appEnv === 'local' ? defaultLocalApiUrl : '');

  return validateFrontendRuntimeConfig(
    {
      appEnv,
      apiBaseUrl,
      firebase: {
        apiKey: env.REACT_APP_FIREBASE_API_KEY,
        authDomain: env.REACT_APP_FIREBASE_AUTH_DOMAIN,
        projectId: env.REACT_APP_FIREBASE_PROJECT_ID,
        storageBucket: env.REACT_APP_FIREBASE_STORAGE_BUCKET,
        messagingSenderId: env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
        appId: env.REACT_APP_FIREBASE_APP_ID,
        ...(hasValue(env.REACT_APP_FIREBASE_MEASUREMENT_ID)
          ? { measurementId: env.REACT_APP_FIREBASE_MEASUREMENT_ID.trim() }
          : {}),
      },
      ...(hasValue(env.REACT_APP_STRIPE_PUBLISHABLE_KEY)
        ? { stripePublishableKey: env.REACT_APP_STRIPE_PUBLISHABLE_KEY.trim() }
        : {}),
    },
    { currentLocation }
  );
}

let activeRuntimeConfig = null;
const listeners = new Set();

function notifyListeners(config) {
  for (const listener of listeners) {
    listener(config);
  }
}

export function onRuntimeConfigChange(listener) {
  if (typeof listener !== 'function') return () => {};
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function setRuntimeConfig(
  rawPayload,
  {
    windowObj = typeof window !== 'undefined' ? window : null,
    currentLocation = windowObj?.location || null,
  } = {}
) {
  const validated = validateFrontendRuntimeConfig(rawPayload, { currentLocation });
  activeRuntimeConfig = validated;
  if (windowObj) {
    windowObj.__FEATHERGLASS_RUNTIME_CONFIG__ = validated;
    delete windowObj.__FEATHERGLASS_RUNTIME_CONFIG_ERROR__;
  }
  notifyListeners(validated);
  return validated;
}

export function getRuntimeConfig({
  windowObj = typeof window !== 'undefined' ? window : null,
  env = typeof process !== 'undefined' ? process.env : {},
  throwOnInvalid = false,
} = {}) {
  if (activeRuntimeConfig) {
    return activeRuntimeConfig;
  }

  const currentLocation = windowObj?.location || null;

  if (windowObj && windowObj.__FEATHERGLASS_RUNTIME_CONFIG__ !== undefined && windowObj.__FEATHERGLASS_RUNTIME_CONFIG__ !== null) {
    try {
      return setRuntimeConfig(windowObj.__FEATHERGLASS_RUNTIME_CONFIG__, { windowObj, currentLocation });
    } catch (error) {
      if (throwOnInvalid) throw error;
      return null;
    }
  }

  try {
    const envConfig = buildRuntimeConfigFromEnv(env, currentLocation);
    if (envConfig) {
      activeRuntimeConfig = envConfig;
      if (windowObj) {
        windowObj.__FEATHERGLASS_RUNTIME_CONFIG__ = envConfig;
      }
      notifyListeners(envConfig);
      return envConfig;
    }
  } catch (error) {
    if (throwOnInvalid) throw error;
    return null;
  }

  return null;
}

export function requireRuntimeConfig(options = {}) {
  const resolved = getRuntimeConfig({ ...options, throwOnInvalid: true });
  if (!resolved) {
    throw frontendConfigError(
      'Missing required frontend runtime configuration: load /config.json or provide complete runtime configuration before initializing browser services.'
    );
  }
  return resolved;
}

export async function loadRuntimeConfig({
  fetchImpl = typeof fetch === 'function' ? fetch : null,
  windowObj = typeof window !== 'undefined' ? window : null,
  env = typeof process !== 'undefined' ? process.env : {},
  forceRefresh = false,
} = {}) {
  const currentLocation = windowObj?.location || null;

  if (!forceRefresh && activeRuntimeConfig) {
    return activeRuntimeConfig;
  }

  if (
    !forceRefresh &&
    windowObj &&
    windowObj.__FEATHERGLASS_RUNTIME_CONFIG__ !== undefined &&
    windowObj.__FEATHERGLASS_RUNTIME_CONFIG__ !== null
  ) {
    return setRuntimeConfig(windowObj.__FEATHERGLASS_RUNTIME_CONFIG__, { windowObj, currentLocation });
  }

  const candidateUrls = ['/config.json'];
  if (currentLocation && isLocalHostname(currentLocation.hostname) && currentLocation.port !== '8080') {
    const protocol = currentLocation.protocol === 'https:' ? 'https:' : 'http:';
    candidateUrls.push(`${protocol}//${currentLocation.hostname}:8080/config.json`);
  }

  let lastError = null;
  if (typeof fetchImpl === 'function') {
    for (const url of candidateUrls) {
      try {
        const response = await fetchImpl(url, {
          method: 'GET',
          headers: { Accept: 'application/json' },
          cache: 'no-store',
          credentials: 'same-origin',
        });

        if (!response || !response.ok) {
          lastError = frontendConfigError(
            `Runtime configuration request to ${url} failed with HTTP ${response?.status || 'unknown'}.`
          );
          continue;
        }

        const payload = await response.json();
        return setRuntimeConfig(payload, { windowObj, currentLocation });
      } catch (error) {
        if (error?.code === 'FRONTEND_RUNTIME_CONFIGURATION') {
          lastError = error;
          // If the endpoint returned an invalid config object (not a network/HTML parse fallback), fail closed immediately.
          if (!/Runtime configuration request to/.test(error.message)) {
            throw error;
          }
        } else {
          lastError = frontendConfigError(
            `Unable to parse JSON runtime configuration from ${url}: ${error?.message || 'invalid response'}.`
          );
        }
      }
    }
  }

  const envFallback = buildRuntimeConfigFromEnv(env, currentLocation);
  if (envFallback) {
    activeRuntimeConfig = envFallback;
    if (windowObj) {
      windowObj.__FEATHERGLASS_RUNTIME_CONFIG__ = envFallback;
    }
    notifyListeners(envFallback);
    return envFallback;
  }

  throw (
    lastError ||
    frontendConfigError(
      'Unable to load frontend runtime configuration from /config.json and no valid runtime configuration was provided.'
    )
  );
}

export function resetRuntimeConfigForTesting({
  windowObj = typeof window !== 'undefined' ? window : null,
} = {}) {
  activeRuntimeConfig = null;
  if (windowObj) {
    delete windowObj.__FEATHERGLASS_RUNTIME_CONFIG__;
    delete windowObj.__FEATHERGLASS_RUNTIME_CONFIG_ERROR__;
  }
  notifyListeners(null);
}

export {
  VALID_APP_ENVS,
  LEGACY_TRANSITION_FIREBASE_PROJECT_ID,
  LEGACY_TRANSITION_PROJECT_NUMBER,
  LEGACY_TRANSITION_STORAGE_BUCKETS,
  FORBIDDEN_PROJECT_IDS,
  FORBIDDEN_PROJECT_NUMBERS,
  FORBIDDEN_FIREBASE_API_KEYS,
};
